import React, { useState, Component } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { MinusIcon, PlusIcon, PlusCircleIcon, TrashIcon } from 'lucide-react';
import { inquiryFormSchema, InquiryFormData } from '../../lib/validation';
import { products } from '../../data/products';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Select } from '../ui/Select';
import { Button } from '../ui/Button';
import { Tabs } from '../ui/Tabs';
interface InquiryFormProps {
  preselectedProductId?: string;
  onSuccess: () => void;
}
interface OrderLine {
  id: string;
  productId: string;
  quantity: number;
  batteries: number;
}
function withVat(price: number) {
  return Math.round(price * 1.2);
}
function getBatteryPrice(productId: string) {
  const p = products.find(x => x.id === productId);
  return p?.series === 'scc' ? 375 : 250;
}
function lineTotal(line: OrderLine) {
  const p = products.find(x => x.id === line.productId);
  if (!p) return 0;
  return p.price * line.quantity + getBatteryPrice(line.productId) * line.batteries;
}
let lineIdCounter = 1;
export function InquiryForm({
  preselectedProductId,
  onSuccess
}: InquiryFormProps) {
  const [customerType, setCustomerType] = useState<'individual' | 'business'>('business');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [orderLines, setOrderLines] = useState<OrderLine[]>([{
    id: String(lineIdCounter++),
    productId: preselectedProductId || '',
    quantity: 1,
    batteries: 0
  }]);
  const {
    register,
    handleSubmit,
    formState: {
      errors
    },
    setValue
  } = useForm<InquiryFormData>({
    resolver: zodResolver(inquiryFormSchema),
    defaultValues: {
      customerType: 'business',
      spareBatteries: 0,
      quantity: 1,
      productId: preselectedProductId || ''
    }
  });
  const handleCustomerTypeChange = (tabId: string) => {
    const type = tabId as 'individual' | 'business';
    setCustomerType(type);
    setValue('customerType', type);
  };
  const updateLine = (id: string, field: keyof OrderLine, value: string | number) => {
    setOrderLines(lines => lines.map(l => l.id === id ? {
      ...l,
      [field]: value
    } : l));
  };
  const addLine = () => {
    setOrderLines(lines => [...lines, {
      id: String(lineIdCounter++),
      productId: '',
      quantity: 1,
      batteries: 0
    }]);
  };
  const removeLine = (id: string) => {
    setOrderLines(lines => lines.filter(l => l.id !== id));
  };
  const grandTotal = orderLines.reduce((sum, l) => sum + lineTotal(l), 0);
  const hasAnyProduct = orderLines.some(l => l.productId !== '');
  const productSelectOptions = [{
    value: '',
    label: 'Izaberite model'
  }, ...products.map(p => {
    const disabled = p.soldOut || p.comingSoon;
    const badgeText = p.soldOut ? 'Prodato' : p.comingSoon ? 'Uskoro dostupno' : '';
    return {
      value: disabled ? '' : p.id,
      label: disabled ? `${p.name} (${p.model}) — ${badgeText}` : `${p.name} (${p.model}) — €${p.price.toLocaleString()} bez PDV`,
      disabled: disabled
    };
  })];
  const counterBtn = 'w-8 h-8 bg-dark-surface border border-dark-border rounded flex items-center justify-center text-white hover:border-brand-yellow disabled:opacity-40 disabled:cursor-not-allowed transition-colors';
  const onSubmit = async (data: InquiryFormData) => {
    setIsSubmitting(true);
    let emailBody = `D-TECH Solutions Upit\n\n`;
    emailBody += `Tip kupca: ${data.customerType === 'individual' ? 'Fizičko lice' : 'Pravno lice'}\n\n`;
    emailBody += `NARUDŽBINA:\n`;
    emailBody += `${'─'.repeat(40)}\n`;
    let grandTotalEmail = 0;
    orderLines.forEach((line, idx) => {
      const p = products.find(x => x.id === line.productId);
      if (!p) return;
      const bPrice = getBatteryPrice(line.productId);
      const lt = lineTotal(line);
      grandTotalEmail += lt;
      emailBody += `\n${idx + 1}. ${p.name} (${p.model})\n`;
      emailBody += `   Cena: €${p.price.toLocaleString()} bez PDV × ${line.quantity} kom = €${(p.price * line.quantity).toLocaleString()} bez PDV\n`;
      if (line.batteries > 0) {
        emailBody += `   Rezervne baterije: ${line.batteries} × €${bPrice} = €${(bPrice * line.batteries).toLocaleString()} bez PDV\n`;
      }
      emailBody += `   Ukupno: €${lt.toLocaleString()} bez PDV (€${withVat(lt).toLocaleString()} sa PDV)\n`;
    });
    emailBody += `\n${'─'.repeat(40)}\n`;
    emailBody += `UKUPNO: €${grandTotalEmail.toLocaleString()} bez PDV (€${withVat(grandTotalEmail).toLocaleString()} sa PDV)\n`;
    emailBody += `\n---\n\n`;
    if (data.customerType === 'individual') {
      emailBody += `Ime: ${data.name}\nEmail: ${data.email}\nTelefon: ${data.phone}\nGrad: ${data.city}\n`;
    } else {
      emailBody += `Kompanija: ${data.companyName}\nPIB: ${data.pibVat}\nMatični broj: ${data.maticniBroj}\nAdresa: ${data.address}\nGrad: ${data.city}\nKontakt: ${data.contactPerson}\nEmail: ${data.email}\nTelefon: ${data.phone}\n`;
    }
    if (data.message) emailBody += `\nPoruka:\n${data.message}\n`;
    const firstProduct = products.find(x => x.id === orderLines[0]?.productId);
    window.location.href = `mailto:info@dtech.rs?subject=Upit za D-TECH opremu&body=${encodeURIComponent(emailBody)}`;
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
    }, 1000);
  };
  if (submitSuccess) {
    return <div className="text-center py-8" data-id="element-47">
        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-48">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-49">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-50" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-white mb-2" data-id="element-51">Hvala vam!</h3>
        <p className="text-gray-300 mb-4" data-id="element-52">
          Vaš upit je primljen. Uskoro ćemo vas kontaktirati.
        </p>
        <p className="text-sm text-brand-yellow font-semibold" data-id="element-53">
          Rok isporuke: ~2 meseca od potvrde porudžbine
        </p>
        <Button variant="primary" size="md" onClick={onSuccess} className="mt-6" data-id="element-54">
          Zatvori
        </Button>
      </div>;
  }
  const orderSection = <div className="space-y-3" data-id="element-55">
      <label className="block text-sm font-semibold text-white" data-id="element-56">
        Mašine i oprema *
      </label>

      {orderLines.map((line, idx) => {
      const selectedProduct = products.find(p => p.id === line.productId);
      const bPrice = getBatteryPrice(line.productId);
      const lt = lineTotal(line);
      return <div key={line.id} className="bg-dark-bg border border-dark-border rounded-industrial p-4 space-y-3" data-id="element-57">
            {/* Header row */}
            <div className="flex items-center justify-between" data-id="element-58">
              <span className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-59">
                Stavka {idx + 1}
              </span>
              {orderLines.length > 1 && <button type="button" onClick={() => removeLine(line.id)} className="text-gray-600 hover:text-red-400 transition-colors" aria-label="Ukloni stavku" data-id="element-60">
                  <TrashIcon className="w-4 h-4" data-id="element-61" />
                </button>}
            </div>

            {/* Product select */}
            <select value={line.productId} onChange={e => updateLine(line.id, 'productId', e.target.value)} className="w-full px-3 py-2.5 bg-dark-surface text-white border border-dark-border rounded-industrial text-sm focus:outline-none focus:border-brand-yellow transition-colors appearance-none" data-id="element-62">
              {productSelectOptions.map(opt => <option key={opt.value + opt.label} value={opt.value} disabled={opt.disabled} data-id="element-63">
                  {opt.label}
                </option>)}
            </select>

            {selectedProduct && <>
                {/* Quantity + batteries row */}
                <div className="grid grid-cols-2 gap-3" data-id="element-64">
                  <div data-id="element-65">
                    <p className="text-xs text-gray-500 mb-1.5" data-id="element-66">
                      Količina mašina
                    </p>
                    <div className="flex items-center gap-2" data-id="element-67">
                      <button type="button" onClick={() => updateLine(line.id, 'quantity', Math.max(1, line.quantity - 1))} disabled={line.quantity === 1} className={counterBtn} data-id="element-68">
                        <MinusIcon className="w-3.5 h-3.5" data-id="element-69" />
                      </button>
                      <span className="text-lg font-bold text-white w-8 text-center" data-id="element-70">
                        {line.quantity}
                      </span>
                      <button type="button" onClick={() => updateLine(line.id, 'quantity', line.quantity + 1)} className={counterBtn} data-id="element-71">
                        <PlusIcon className="w-3.5 h-3.5" data-id="element-72" />
                      </button>
                    </div>
                  </div>
                  <div data-id="element-73">
                    <p className="text-xs text-gray-500 mb-1.5" data-id="element-74">
                      Rezervne baterije
                      <span className="text-gray-600 ml-1" data-id="element-75">(€{bPrice})</span>
                    </p>
                    <div className="flex items-center gap-2" data-id="element-76">
                      <button type="button" onClick={() => updateLine(line.id, 'batteries', Math.max(0, line.batteries - 1))} disabled={line.batteries === 0} className={counterBtn} data-id="element-77">
                        <MinusIcon className="w-3.5 h-3.5" data-id="element-78" />
                      </button>
                      <span className="text-lg font-bold text-white w-8 text-center" data-id="element-79">
                        {line.batteries}
                      </span>
                      <button type="button" onClick={() => updateLine(line.id, 'batteries', line.batteries + 1)} className={counterBtn} data-id="element-80">
                        <PlusIcon className="w-3.5 h-3.5" data-id="element-81" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Line total */}
                <div className="flex items-baseline justify-between pt-1 border-t border-dark-border" data-id="element-82">
                  <span className="text-xs text-gray-500" data-id="element-83">Stavka ukupno</span>
                  <div className="text-right" data-id="element-84">
                    <span className="text-brand-yellow font-bold" data-id="element-85">
                      €{lt.toLocaleString()}
                    </span>
                    <span className="text-xs text-gray-500 ml-1" data-id="element-86">bez PDV</span>
                    <p className="text-xs text-gray-600" data-id="element-87">
                      sa PDV: €{withVat(lt).toLocaleString()}
                    </p>
                  </div>
                </div>
              </>}
          </div>;
    })}

      {/* Add line button */}
      <button type="button" onClick={addLine} className="w-full flex items-center justify-center gap-2 py-2.5 border border-dashed border-dark-border rounded-industrial text-sm text-gray-500 hover:text-brand-yellow hover:border-brand-yellow transition-colors" data-id="element-88">
        <PlusCircleIcon className="w-4 h-4" data-id="element-89" />
        Dodaj još jednu mašinu
      </button>

      {/* Grand total */}
      {hasAnyProduct && <div className="p-4 bg-brand-yellow bg-opacity-10 border-2 border-brand-yellow rounded-industrial" data-id="element-90">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-1" data-id="element-91">
            Ukupna cena narudžbine
          </p>
          <p className="text-2xl font-black text-brand-yellow" data-id="element-92">
            €{grandTotal.toLocaleString()}
            <span className="text-sm font-normal text-gray-400 ml-2" data-id="element-93">
              bez PDV
            </span>
          </p>
          <p className="text-xs text-gray-500 mt-0.5" data-id="element-94">
            Sa PDV: €{withVat(grandTotal).toLocaleString()}
          </p>
        </div>}
    </div>;
  const businessForm = <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-id="element-95">
      <input type="hidden" {...register('customerType')} value="business" data-id="element-96" />
      {orderSection}
      <div className="border-t-2 border-dark-border pt-6" data-id="element-97">
        <h3 className="text-lg font-bold text-white mb-4" data-id="element-98">
          Podaci o kompaniji
        </h3>
        <div className="space-y-4" data-id="element-99">
          <Input label="Naziv kompanije *" type="text" {...register('companyName')} error={errors.companyName?.message} placeholder="Naziv vaše kompanije" data-id="element-100" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-101">
            <Input label="PIB *" type="text" {...register('pibVat')} error={errors.pibVat?.message} placeholder="123456789" data-id="element-102" />
            <Input label="Matični broj *" type="text" {...register('maticniBroj')} error={errors.maticniBroj?.message} placeholder="12345678" data-id="element-103" />
          </div>
          <Input label="Adresa *" type="text" {...register('address')} error={errors.address?.message} placeholder="Ulica i broj" data-id="element-104" />
          <Input label="Grad *" type="text" {...register('city')} error={errors.city?.message} placeholder="Grad" data-id="element-105" />
          <Input label="Kontakt osoba *" type="text" {...register('contactPerson')} error={errors.contactPerson?.message} placeholder="Ime i prezime" data-id="element-106" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-107">
            <Input label="Email *" type="email" {...register('email')} error={errors.email?.message} placeholder="kontakt@kompanija.com" data-id="element-108" />
            <Input label="Telefon *" type="tel" {...register('phone')} error={errors.phone?.message} placeholder="+381 11 123 4567" data-id="element-109" />
          </div>
          <Textarea label="Poruka (opciono)" {...register('message')} error={errors.message?.message} placeholder="Dodatne informacije ili pitanja" rows={3} data-id="element-110" />
        </div>
      </div>
      <Button type="submit" variant="primary" size="lg" disabled={isSubmitting} className="w-full" data-id="element-111">
        {isSubmitting ? 'Šaljem...' : 'Pošalji upit'}
      </Button>
    </form>;
  const individualForm = <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-id="element-112">
      <input type="hidden" {...register('customerType')} value="individual" data-id="element-113" />
      {orderSection}
      <div className="border-t-2 border-dark-border pt-6" data-id="element-114">
        <h3 className="text-lg font-bold text-white mb-4" data-id="element-115">Vaši podaci</h3>
        <div className="space-y-4" data-id="element-116">
          <Input label="Ime i prezime *" type="text" {...register('name')} error={errors.name?.message} placeholder="Vaše ime i prezime" data-id="element-117" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-118">
            <Input label="Email *" type="email" {...register('email')} error={errors.email?.message} placeholder="vas.email@primer.com" data-id="element-119" />
            <Input label="Telefon *" type="tel" {...register('phone')} error={errors.phone?.message} placeholder="+381 60 123 4567" data-id="element-120" />
          </div>
          <Input label="Grad *" type="text" {...register('city')} error={errors.city?.message} placeholder="Vaš grad" data-id="element-121" />
          <Textarea label="Poruka (opciono)" {...register('message')} error={errors.message?.message} placeholder="Dodatne informacije ili pitanja" rows={3} data-id="element-122" />
        </div>
      </div>
      <Button type="submit" variant="primary" size="lg" disabled={isSubmitting} className="w-full" data-id="element-123">
        {isSubmitting ? 'Šaljem...' : 'Pošalji upit'}
      </Button>
    </form>;
  return <Tabs tabs={[{
    id: 'business',
    label: 'Pravno lice',
    content: businessForm
  }, {
    id: 'individual',
    label: 'Fizičko lice',
    content: individualForm
  }]} defaultTab="business" onChange={handleCustomerTypeChange} data-id="element-124" />;
}